import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  buildTableMatrix,
  sanitizeExportFilename,
} from "utils/buildTableMatrix";
import {
  downloadTableAsCsv,
  downloadTableAsJson,
  downloadTableAsXml,
} from "utils/tableExport";
import "../DataExportActions/DataExportActions.scss";

const FORMATS = [
  {
    id: "csv",
    labelId: "export.table.csv",
    Icon: GridOnOutlinedIcon,
    modifier: "csv",
  },
  {
    id: "json",
    labelId: "export.table.json",
    Icon: DataObjectOutlinedIcon,
    modifier: "json",
  },
  {
    id: "xml",
    labelId: "export.table.xml",
    Icon: CodeOutlinedIcon,
    modifier: "xml",
  },
];

function TableExportActions({
  columns = [],
  rows = [],
  yearType = null,
  currentLocale = "ar",
  filename = "export",
  tableId = "table-to-xls",
  excelSheet = "tablexls",
  excelImage,
  className = "",
  metadata = {},
}) {
  const intl = useIntl();

  const safeFilename = useMemo(
    () => sanitizeExportFilename(filename),
    [filename]
  );

  const matrix = useMemo(
    () =>
      buildTableMatrix({
        columns,
        rows,
        yearType,
        currentLocale,
      }),
    [columns, rows, yearType, currentLocale]
  );

  const hasData = matrix.headers.length > 0 && matrix.rows.length > 0;
  const disabledTitle = hasData
    ? undefined
    : intl.formatMessage({ id: "export.noData" });

  const handleExport = (format) => {
    if (!hasData) return;

    const exportMeta = {
      ...metadata,
      title: metadata.title || safeFilename,
    };

    if (format === "csv") {
      downloadTableAsCsv(matrix, safeFilename);
      return;
    }

    if (format === "json") {
      downloadTableAsJson(matrix, safeFilename, exportMeta);
      return;
    }

    if (format === "xml") {
      downloadTableAsXml(matrix, safeFilename, {
        ...exportMeta,
        rootName: safeFilename,
      });
    }
  };

  return (
    <div className={`data-export-actions ${className}`.trim()}>
      <div className="data-export-actions__header">
        <FileDownloadOutlinedIcon className="data-export-actions__header-icon" />
        <span className="data-export-actions__header-text">
          <FormattedMessage id="export.table.formats" />
        </span>
      </div>

      <div className="data-export-actions__buttons">
        <div
          className={`data-export-actions__btn data-export-actions__btn--excel${
            !hasData ? " data-export-actions__btn--disabled" : ""
          }`}
          title={disabledTitle}
          aria-disabled={!hasData}
        >
          {hasData ? (
            <ReactHTMLTableToExcel
              id="table-export-excel-button"
              className="data-export-actions__excel-trigger"
              table={tableId}
              filename={safeFilename}
              sheet={excelSheet}
              buttonText={
                <span className="data-export-actions__excel-content">
                  {excelImage ? (
                    <img src={excelImage} alt="" aria-hidden="true" />
                  ) : (
                    <TableChartOutlinedIcon fontSize="small" />
                  )}
                  <span>
                    <FormattedMessage id="export.table.excel" />
                  </span>
                </span>
              }
            />
          ) : (
            <span
              className="data-export-actions__excel-trigger data-export-actions__excel-content"
              aria-label={intl.formatMessage({ id: "export.table.excel" })}
            >
              {excelImage ? (
                <img src={excelImage} alt="" aria-hidden="true" />
              ) : (
                <TableChartOutlinedIcon fontSize="small" />
              )}
              <span>
                <FormattedMessage id="export.table.excel" />
              </span>
            </span>
          )}
        </div>

        {FORMATS.map(({ id, labelId, Icon, modifier }) => (
          <button
            key={id}
            type="button"
            className={`data-export-actions__btn data-export-actions__btn--${modifier}${
              !hasData ? " data-export-actions__btn--disabled" : ""
            }`}
            onClick={() => handleExport(id)}
            disabled={!hasData}
            title={disabledTitle || intl.formatMessage({ id: labelId })}
            aria-label={intl.formatMessage({ id: labelId })}
          >
            <Icon className="data-export-actions__format-icon" fontSize="small" />
            <span>
              <FormattedMessage id={labelId} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default TableExportActions;
